/**
 * Custom hook that encapsulates all major user actions, such as saving,
 * finishing, and handling confirmation modals. It interacts with data services
 * and manages the 'saving' state to provide feedback to the UI.
 */

import { useState, useRef, useMemo } from 'react';
import { toast } from '../../../Common/ToastNotification';

//Legacy code
import listService from "ListService";
import mobileListService from "../../../../service/mobile/MobileListService";
import restService from 'RestService';
import PopupWrapperService from 'PopupWrapperService';

export const useWizardActions = ({
    save, layout, setIsDirty, requestClose,
    activePage, selectedSectionId, selectedControlId, availableControls,
    addAllControls, removeAllControls, deleteSection, removeControl,
    moveControlUp, moveControlDown, moveSectionUp, moveSectionDown,
    setSelectedSectionId, orientation, device
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);
    const [isBackgrounded, setIsBackgrounded] = useState(false);
    const closeReason = useRef('cancel'); // Tracks if the wizard was closed intentionally or canceled

    /*
        FOOTER BUTTONS HANDLERS
    */
    //Handles the simple 'Save' action
    const handleSaveClick = async () => {
        setIsSaving(true);
        setIsBackgrounded(true);
        const result = await save();

        if (result.message !== -1) {
            toast.show({
                type: result.success ? 'success' : 'error',
                message: result.message,
            });
        }
        setIsBackgrounded(false);
        setIsSaving(false);
        return result.success;
    };

    //Handles the 'preview' in player action
    const handlePreviewClick = async () => {
        const saveSuccessful = await handleSaveClick();
        if (!saveSuccessful) {
            toast.show({ type: 'error', message: 'Could not save changes before preview.' });
            return;
        }

        setIsBackgrounded(true);

        try {
            const previewUrl = restService.getMobilePreviewLink(device, orientation);
            PopupWrapperService.windowOpen(previewUrl);
        } catch (error) {
            console.error("The preview function failed:", error);
            toast.show({ type: 'error', message: `Preview failed: ${error.message || 'Unknown error'}` });
        } finally {
            setIsBackgrounded(false);
        }
    };

    //Executes the full 'Finish' sequence after user confirmation.Includes saving, legacy service calls, and closing the wizard.
    const executeFinish = async () => {
        setConfirmModal(null);
        setIsSaving(true);
        setIsBackgrounded(true);
        try {
                        
            // Legacy Service Calls
            listService.mobileSetWizardCompleted = true;
            listService.timesWizardCompleted = (listService.timesWizardCompleted || 0) + 1;
           
            const saveResult = await save(true);
            
            if (!saveResult.success) {
                toast.show({ type: 'error', message: `Save failed: ${saveResult.message}` });
                return;
            }
            toast.show({ type: 'success', message: saveResult.message });

            listService.updateDefaultColumnLayout();
            listService.removeMobileNewObjects();
            mobileListService.updateMobileObjMap();
            
            closeReason.current = 'finish';
            setIsDirty(false);
                
            setTimeout(() => {
                if (requestClose) requestClose({ skipDirtyCheck: true });
            }, 100);

        } catch (errorInfo) {
            const message = Array.isArray(errorInfo) && errorInfo.length > 0 ? errorInfo[0] : 'An unknown error occurred.';
            toast.show({ type: 'error', message: `Save failed: ${message}` });
        } finally {
            setIsBackgrounded(false);
            setIsSaving(false);
        }
    };

    //Validates the layout and triggers the confirmation modal for finishing.
    const handleFinishClick = () => {
        const allSections = Object.values(layout || {}).flat();
        const isAtLeastOneControlPlaced = allSections.some(section => section.controls.length > 0);

        if (!isAtLeastOneControlPlaced) {
            toast.show({ type: 'error', message: 'Please add at least one control before finishing.' });
            return;
        }

        setConfirmModal({
            title: 'Finish Mobile App Design',
            children: 'Are you sure you want to save and finish?',
            onClose: () => setConfirmModal(null),
            buttons: [
                { label: 'Cancel', variant: 'gray', onClick: () => setConfirmModal(null) },
                { label: 'Save & Finish', variant: 'orange', onClick: executeFinish }
            ]
        });
    };

    /*
        APP TOOLBAR ACTIONS
    */
    const handleAddAll = () => {

        const pageControls = availableControls[activePage.id] || [];
        const unplacedControls = pageControls.filter(c => !c.isPlaced);

        if (unplacedControls.length === 0) {
            toast.show({ type: 'info', message: 'All desktop controls for this page have already been placed.' });
            return;
        }

        const sections = layout[activePage.id] || [];
        if (sections.length === 0) {
            toast.show({ type: 'info', message: 'Please add a section first.' });
            return;
        }
        if (!selectedSectionId) {
            toast.show({ type: 'info', message: 'Please select a section to add controls to.' });
            return;
        }
        setConfirmModal({
            title: 'Add All Controls',
            children: 'Are you sure you want to add all remaining desktop controls to the selected section?',
            onClose: () => setConfirmModal(null),
            buttons: [
                {
                    label: 'Cancel',
                    variant: 'gray',
                    onClick: () => setConfirmModal(null),
                },
                {
                    label: 'Add All',
                    variant: 'orange',
                    onClick: () => {
                        addAllControls({ pageId: activePage.id, sectionId: selectedSectionId });
                        setConfirmModal(null);
                    },
                }
            ],
        });
    };

    const handleRemoveAll = () => {
        const isSectionSelected = !!selectedSectionId;
        const targetId = isSectionSelected ? selectedSectionId : activePage.id;
        const pageLayout = layout[activePage.id] || [];

        if (isSectionSelected) {
            const targetSection = pageLayout.find(s => s.id === selectedSectionId);
            const controlsCount = targetSection ? targetSection.controls.length : 0;

            if (controlsCount === 0) {
                toast.show({ type: 'info', message: 'This section is already empty.' });
                return;
            }
        } else {
            const totalControlsOnPage = pageLayout.flatMap(s => s.controls).length;

            if (totalControlsOnPage === 0) {
                toast.show({ type: 'info', message: 'There are no controls on this page to remove.' });
                return;
            }
        }

        setConfirmModal({
            title: isSectionSelected ? 'Remove Controls From Section' : 'Remove All Controls',
            children: isSectionSelected
                ? 'Are you sure you want to remove all controls from this section?'
                : 'Are you sure you want to remove all controls from this page?',
            onClose: () => setConfirmModal(null),
            buttons: [
                { label: 'Cancel', onClick: () => setConfirmModal(null) },
                {
                    label: 'Confirm',
                    variant: 'orange',
                    onClick: () => {
                        removeAllControls({ pageId: activePage.id, sectionId: selectedSectionId });
                        setConfirmModal(null);
                    },
                },
            ],
        });
    };

    const handleDeleteSection = () => {
        if (!selectedSectionId || !activePage) return;

        const pageLayout = layout[activePage.id];
        const section = pageLayout?.find(s => s.id === selectedSectionId);

        if (section && section.controls.length > 0) {
            setConfirmModal({
                title: 'Delete Section',
                children: 'Are you sure you want to delete this section? All controls inside it will be returned to the desktop panel.',
                onClose: () => setConfirmModal(null),
                buttons: [
                    { label: 'Cancel', onClick: () => setConfirmModal(null) },
                    {
                        label: 'Delete',
                        variant: 'orange',
                        onClick: () => {
                            deleteSection({ pageId: activePage.id, sectionId: selectedSectionId });
                            setSelectedSectionId(null);
                            setConfirmModal(null);
                        },
                    },
                ],
            });

        } else {
            deleteSection({ pageId: activePage.id, sectionId: selectedSectionId });
            setSelectedSectionId(null);
        }
    };

    /*
        COLUMN TOOLBAR ACTIONS
    */
    const toolbarActions = useMemo(() => {
        if (!activePage || !layout) return null;

        const isControlOnMobile = layout[activePage.id]?.some(s => s.controls.some(c => c.id === selectedControlId));

        if (selectedControlId && isControlOnMobile) {// Move/remove objects
            return {
                onRemove: (e) => {
                    e.stopPropagation();
                    removeControl({ pageId: activePage.id, controlId: selectedControlId });
                },
                onMoveUp: (e) => {
                    e.stopPropagation();
                    moveControlUp({ pageId: activePage.id, controlId: selectedControlId });
                },
                onMoveDown: (e) => {
                    e.stopPropagation();
                    moveControlDown({ pageId: activePage.id, controlId: selectedControlId });
                },
            };
        }
        if (selectedSectionId) { // Move/remove sections
            return {
                onRemove: (e) => {
                    e.stopPropagation();
                    handleDeleteSection();
                },
                onMoveUp: (e) => {
                    e.stopPropagation();
                    moveSectionUp({ pageId: activePage.id, sectionId: selectedSectionId });
                },
                onMoveDown: (e) => {
                    e.stopPropagation();
                    moveSectionDown({ pageId: activePage.id, sectionId: selectedSectionId });
                },
            };
        }
        return null;
    }, [activePage, selectedControlId, selectedSectionId, layout, removeControl, moveControlUp, moveControlDown, moveSectionUp, moveSectionDown]);

    return {
        isSaving,
        confirmModal,
        closeReason,
        isBackgrounded,
        handleSaveClick,
        handlePreviewClick,
        handleFinishClick,
        handleAddAll,
        handleRemoveAll,
        toolbarActions,
        handleDeleteSection
    };
};